import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useOperationalStore, { PIPELINE, IDLE_MS, getStageAge, BLOCKER_LABELS } from '../platform/store/useOperationalStore';
import useShopSettings, { getConfiguredPipeline } from '../platform/settings/useShopSettings';
import FullscreenBtn from '../components/FullscreenBtn';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import { EVENT_TYPES, recordTruckEvent } from '../platform/events/operationEvents';
import { getCurrentRole, hasPermission, PERMISSIONS } from '../shared/permissions';
import NewROModal from '../components/Modals/NewROModal';
import AssignTechModal from '../components/Modals/AssignTechModal';
import NoteModal from '../components/Modals/NoteModal';
import FlowSwitcher from '../platform/shell/FlowSwitcher';
import './FlowView.css';

const client = generateClient();

function nowSeconds() {
  return Math.floor(Date.now() / 1000);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDur(ms) {
  if (!ms || ms <= 0) return '—';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0)   return `${h}h ${m}m`;
  return `${m}m`;
}

function stripTypename(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripTypename);
  const { __typename, ...rest } = obj;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, stripTypename(v)]));
}

// Statuses that live in the Parts column
const PARTS_STATUSES = ['parts', 'parts-hold'];

// All ordered parts received — truck is waiting on a tech, not a vendor
function partsIn(truck) {
  if (truck.partsStatus === 'READY') return true;
  const parts = truck.parts || [];
  return parts.length > 0 && parts.every(p => p.status === 'received');
}

function getHealth(truck) {
  const elapsed = Date.now() - ((truck.startTime || 0) * 1000 || Date.now());
  const estMs   = (truck.repairCategories || [])
    .reduce((s, c) => s + (parseFloat(c.estimatedTime) || 0) * 3_600_000, 0);
  if (!estMs) {
    if (elapsed > 8 * 3_600_000) return 'red';
    if (elapsed > 4 * 3_600_000) return 'amber';
    return 'green';
  }
  const pct = elapsed / estMs;
  if (pct >= 1)    return 'red';
  if (pct >= 0.75) return 'amber';
  return 'green';
}

function isIdle(truck) {
  const thr = IDLE_MS[truck.status];
  if (!thr) return false;
  return Date.now() - ((truck.lastStatusChange || 0) * 1000 || Date.now()) > thr;
}

// ─── FlowCard ─────────────────────────────────────────────────────────────────

const BLOCKER_SHORT = {
  waiting_parts:         'PARTS',
  waiting_authorization: 'AUTH',
  waiting_customer:      'CUSTOMER',
  waiting_tech:          'TECH',
  waiting_tow:           'TOW',
  waiting_vendor:        'VENDOR',
  other:                 'BLOCKED',
};

function FlowCard({ truck, isDragging, dropIndicator, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, canClose, canVoid, canEdit, canNote, onCloseRO, onVoidRO, onEditRO, onAssignTech, onAddNote, onPartsIn }) {
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'close' | 'void'
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  function openMenu(e) {
    e.stopPropagation();
    if (menuOpen) { setMenuOpen(false); setConfirming(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    // Menu renders in a portal (fixed): right-aligned to the button, clamped
    // to the viewport so it never clips or hides under anything.
    const MENU_W = 104;
    setMenuPos({
      top: Math.min(rect.bottom + 4, window.innerHeight - 180),
      left: Math.max(8, Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - 8)),
    });
    setConfirming(null);
    setMenuOpen(true);
  }

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onMouse(e) {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setMenuOpen(false);
      setConfirming(null);
    }
    // Any scroll or resize would strand the fixed-position menu — close it.
    function onScroll() { setMenuOpen(false); setConfirming(null); }
    document.addEventListener('mousedown', onMouse);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [menuOpen]);
  const health     = getHealth(truck);
  const idle       = isIdle(truck);
  const stageAge   = getStageAge(truck);
  const now        = Date.now();
  const elapsed    = now - ((truck.startTime || 0) * 1000 || now);
  const stageLabel = PIPELINE.find(p => p.statuses.includes(truck.status))?.label || truck.status;
  const techs      = truck.techNumbers?.filter(Boolean).length
    ? truck.techNumbers.filter(Boolean)
    : truck.techNumber ? [truck.techNumber] : [];

  // Complaint: truck.reason, fallback to make+model
  const complaint = truck.reason
    || [truck.make, truck.model].filter(Boolean).join(' ')
    || null;

  // Active blocker
  const blocker = truck.currentBlocker ?? null;

  // Promise risk based on truck.promisedTime (AWSTimestamp = Unix seconds)
  const promiseRisk = (() => {
    const pt = truck.promisedTime;
    if (!pt) return null;
    const ptMs = pt < 10_000_000_000 ? pt * 1000 : pt;
    const msUntil = ptMs - now;
    if (msUntil < 0)               return 'overdue';
    if (msUntil < 2 * 3_600_000)   return 'soon';
    if (msUntil < 8 * 3_600_000)   return 'today';
    return null;
  })();

  const showAgeFlag = stageAge.level === 'orange' || stageAge.level === 'red';

  return (
    <div
      className={[
        'fc',
        `fc--${health}`,
        `fc--age-${stageAge.level}`,
        idle           ? 'fc--idle'     : '',
        truck.warranty ? 'fc--warranty' : '',
        isDragging     ? 'fc--dragging' : '',
        dropIndicator === 'before' ? 'fc--drop-before' : '',
        dropIndicator === 'after'  ? 'fc--drop-after'  : '',
      ].filter(Boolean).join(' ')}
      draggable
      onClick={() => setSelectedTruckId(truck.id)}
      onContextMenu={canEdit ? (e) => { e.preventDefault(); e.stopPropagation(); onEditRO(truck); } : undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ cursor: 'grab' }}
    >
      <div className="fc-bar" />
      <div className="fc-body">

        {/* 1. Top row: RO (reference only) + promise risk + badges + card menu */}
        <div className="fc-top">
          <span className="fc-ro">{truck.repairOrder}</span>
          {(canClose || canVoid || canEdit || canNote) && (
            <div className="fc-menu-wrap">
              <button
                ref={btnRef}
                className={`fc-menu-btn${menuOpen ? ' fc-menu-btn--open' : ''}`}
                title="RO actions"
                onClick={openMenu}
              >⋯</button>
              {menuOpen && createPortal(
                <div
                  ref={menuRef}
                  className="fc-menu"
                  style={{ top: menuPos.top, left: menuPos.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {confirming === 'close' ? (
                    <>
                      <button className="fc-menu-item fc-menu-item--confirm"
                        onClick={() => { setMenuOpen(false); setConfirming(null); onCloseRO(truck); }}>
                        Confirm close
                      </button>
                      <button className="fc-menu-item" onClick={() => setConfirming(null)}>Cancel</button>
                    </>
                  ) : confirming === 'void' ? (
                    <>
                      <button className="fc-menu-item fc-menu-item--danger"
                        onClick={() => { setMenuOpen(false); setConfirming(null); onVoidRO(truck); }}>
                        Confirm void
                      </button>
                      <button className="fc-menu-item" onClick={() => setConfirming(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      {canEdit && PARTS_STATUSES.includes(truck.status) && !partsIn(truck) && (
                        <button className="fc-menu-item fc-menu-item--confirm"
                          onClick={() => { setMenuOpen(false); onPartsIn(truck); }}>Parts In</button>
                      )}
                      {canEdit && (
                        <button className="fc-menu-item"
                          onClick={() => { setMenuOpen(false); onAssignTech(truck); }}>Assign Tech</button>
                      )}
                      {canNote && (
                        <button className="fc-menu-item"
                          onClick={() => { setMenuOpen(false); onAddNote(truck); }}>Add Note</button>
                      )}
                      {canEdit && (
                        <button className="fc-menu-item"
                          onClick={() => { setMenuOpen(false); onEditRO(truck); }}>✎ Edit RO</button>
                      )}
                      {canClose && (
                        <button className="fc-menu-item" onClick={() => setConfirming('close')}>✓ Close RO</button>
                      )}
                      {canVoid && (
                        <button className="fc-menu-item fc-menu-item--danger" onClick={() => setConfirming('void')}>⊘ Void RO</button>
                      )}
                    </>
                  )}
                </div>,
                document.body
              )}
            </div>
          )}
          <div className="fc-badges">
            {promiseRisk === 'overdue' && (
              <span className="fc-badge fc-badge--late" title="Promise overdue">LATE</span>
            )}
            {promiseRisk === 'soon' && (
              <span className="fc-badge fc-badge--soon" title="Promise due within 2 hours">◉</span>
            )}
            {promiseRisk === 'today' && (
              <span className="fc-badge fc-badge--today" title="Promise due today">◉</span>
            )}
            {PARTS_STATUSES.includes(truck.status) && partsIn(truck) && (
              <span className="fc-badge fc-badge--parts-in" title="All parts received — needs a tech">PARTS IN</span>
            )}
            {truck.warranty   && <span className="fc-badge fc-badge--w"    title="Warranty">W</span>}
            {truck.isHardDown && <span className="fc-badge fc-badge--fd"   title="Hard Down">HD</span>}
            {idle             && <span className="fc-badge fc-badge--idle" title="Idle">Idle</span>}
          </div>
        </div>

        {/* 2. Company name — dominant, wall-readable */}
        <div className="fc-company">{truck.companyName || '—'}</div>

        {/* 3. Complaint — what the truck is here for */}
        {complaint && <div className="fc-complaint">{complaint}</div>}

        {/* 4. Tech + Unit (compact reference row) */}
        <div className="fc-meta-row">
          <span className="fc-unit">{truck.truckNumber || '—'}</span>
          <div className="fc-techs">
            {techs.map(n => <span key={n} className="fc-tech">{n}</span>)}
          </div>
        </div>

        {/* 5. Age/time row */}
        <div className="fc-time">
          <span className="fc-time-total">{fmtDur(elapsed)}</span>
          <span className="fc-time-sep">·</span>
          <span className={`fc-time-stage fc-time-stage--${stageAge.level}`}>
            {fmtDur(stageAge.stageMs)} here
          </span>
        </div>

        {/* 6+7. Bottom bar(s): age flag and/or blocker — full-width flush stripe */}
        {(showAgeFlag || blocker) && (
          <div className="fc-bottom-bars">
            {showAgeFlag && (
              <div className={`fc-age-flag fc-age-flag--${stageAge.level}`}>
                {fmtDur(stageAge.stageMs)} in {stageLabel}
              </div>
            )}
            {blocker && (
              <div className="fc-blocker" title={BLOCKER_LABELS[blocker.type] || 'Blocked'}>
                ⚠ {BLOCKER_SHORT[blocker.type] || 'BLOCKED'}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Stage accent colors (per spec) ──────────────────────────────────────────

const STAGE_COLORS = {
  'ro-creation': '#64748B',
  'diag':        '#64748B',
  'approval':    '#F59E0B',
  'parts':       '#7C3AED',
  'repair':      '#2563EB',
  'verify':      '#16A34A',
  'pmt':         '#6B7280',
};

// ─── FlowView ─────────────────────────────────────────────────────────────────

export default function FlowView({ onAddTruck }) {
  const trucks           = useOperationalStore(s => s.trucks);
  const localUpdateTruck = useOperationalStore(s => s.localUpdateTruck);
  const flowColumns      = useShopSettings(s => s.settings.flow.columns);

  const [activeStage,    setActiveStage]    = useState(null);
  const [draggingId,     setDraggingId]     = useState(null);
  const [dragOverStage,  setDragOverStage]  = useState(null);
  const [dragOverCardId, setDragOverCardId] = useState(null);
  // State drives the indicator (re-renders when the half flips); the ref
  // mirrors it so async drop handlers always read the current value.
  const [insertBefore,   setInsertBefore]   = useState(true);
  const insertBeforeRef = useRef(true);

  const byStage = useMemo(() => {
    const configuredPipeline = getConfiguredPipeline(PIPELINE, flowColumns);
    const active = trucks.filter(t =>
      PIPELINE.some(p => p.statuses.includes(t.status))
    );
    return configuredPipeline.map(stage => ({
      stage,
      trucks: active
        .filter(t => stage.statuses.includes(t.status))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }));
  }, [trucks, flowColumns]);

  const displayed = activeStage
    ? byStage.filter(g => g.stage.id === activeStage)
    : byStage;

  // ── Column-level drag handlers ────────────────────────────────────────────

  const handleDragStart = useCallback((e, truck) => {
    setDraggingId(truck.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', truck.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverStage(null);
    setDragOverCardId(null);
  }, []);

  const handleColDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  }, []);

  const handleColDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  }, []);

  // Column drop: cross-stage move OR same-stage "send to end" — both append
  // to the end of the target column. (Previously a same-stage drop on empty
  // column space silently did nothing and the card snapped back.)
  const handleColDrop = useCallback(async (e, stage) => {
    e.preventDefault();
    setDragOverStage(null);
    setDraggingId(null);
    const truckId = e.dataTransfer.getData('text/plain');
    if (!truckId) return;

    const truck = trucks.find(t => t.id === truckId);
    if (!truck) return;

    const targetStatus = stage.statuses[0];
    const isSameStage  = truck.status === targetStatus;
    const now = nowSeconds();
    const stageTrucks = trucks.filter(t => stage.statuses.includes(t.status) && t.id !== truckId);
    const endPosition = (stageTrucks.length + 1) * 100;

    if (isSameStage) {
      // Reorder only: drop on the column's empty space sends the card to the end
      const maxPos = stageTrucks.reduce((m, t) => Math.max(m, t.position ?? 0), -1);
      if ((truck.position ?? 0) > maxPos) return; // already last — nothing to do
      localUpdateTruck(truckId, { position: endPosition });
      try {
        await client.graphql({
          query: mutations.updateTruck,
          variables: { input: { id: truckId, position: endPosition } },
        });
      } catch (err) {
        console.error('Reorder save failed:', err);
      }
      return;
    }

    const statusHistory = [...(truck.statusHistory || []), { status: targetStatus, timeEntered: now }];

    localUpdateTruck(truckId, {
      status: targetStatus,
      lastStatusChange: now,
      position: endPosition,
      statusHistory,
    });

    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: {
          input: { id: truckId, status: targetStatus, lastStatusChange: now, position: endPosition, statusHistory },
        },
      });
      recordTruckEvent(truck, EVENT_TYPES.STATUS_CHANGED, {
        timestamp: now,
        previousStatus: truck.status,
        newStatus: targetStatus,
        metadata: { stageId: stage.id, moveSource: 'flow-column-drop' },
      });
    } catch (err) {
      console.error('Drop update failed:', err);
    }
  }, [trucks, localUpdateTruck]);

  // ── Card-level drag handlers (reorder within column) ─────────────────────

  const handleCardDragOver = useCallback((e, cardId, stageId) => {
    e.preventDefault();
    e.stopPropagation(); // consume — prevents column dragOver from clearing card state
    e.dataTransfer.dropEffect = 'move';
    if (cardId === draggingId) {
      // Hovering the card being dragged — no indicator on itself
      setDragOverCardId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const before = e.clientY < rect.top + rect.height / 2;
    insertBeforeRef.current = before;
    setInsertBefore(before);
    setDragOverCardId(cardId);
    // Keep the column highlighted while over its cards, not just its gaps
    setDragOverStage(stageId);
  }, [draggingId]);

  const handleCardDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCardId(null);
    }
  }, []);

  const handleCardDrop = useCallback(async (e, targetCard, stage) => {
    e.preventDefault();
    e.stopPropagation(); // prevent column onDrop from also firing
    setDragOverCardId(null);
    setDragOverStage(null);
    setDraggingId(null);

    const truckId = e.dataTransfer.getData('text/plain');
    if (!truckId || truckId === targetCard.id) return;

    const sourceTruck = trucks.find(t => t.id === truckId);
    if (!sourceTruck) return;

    const targetStatus  = stage.statuses[0];
    const isSameStage   = sourceTruck.status === targetStatus;
    const before        = insertBeforeRef.current;
    const now           = nowSeconds();

    // Build the reordered list for the target stage
    const stageTrucks = trucks
      .filter(t => stage.statuses.includes(t.status) && t.id !== truckId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const targetIdx = stageTrucks.findIndex(t => t.id === targetCard.id);
    const insertIdx = before ? targetIdx : targetIdx + 1;
    stageTrucks.splice(insertIdx, 0, sourceTruck);

    // Assign clean positions: 0, 100, 200, …
    const posUpdates = stageTrucks.map((t, i) => ({ id: t.id, position: i * 100 }));

    // Optimistic update
    const statusHistory = [...(sourceTruck.statusHistory || []), { status: targetStatus, timeEntered: now }];

    posUpdates.forEach(({ id, position }) => {
      if (id === truckId && !isSameStage) {
        localUpdateTruck(id, {
          position,
          status: targetStatus,
          lastStatusChange: now,
          statusHistory,
        });
      } else {
        localUpdateTruck(id, { position });
      }
    });

    // Persist all positions (parallel)
    try {
      await Promise.all(posUpdates.map(({ id, position }) => {
        const input = id === truckId && !isSameStage
          ? { id, position, status: targetStatus, lastStatusChange: now, statusHistory }
          : { id, position };
        return client.graphql({ query: mutations.updateTruck, variables: { input } });
      }));
      if (!isSameStage) {
        recordTruckEvent(sourceTruck, EVENT_TYPES.STATUS_CHANGED, {
          timestamp: now,
          previousStatus: sourceTruck.status,
          newStatus: targetStatus,
          metadata: { stageId: stage.id, moveSource: 'flow-card-drop' },
        });
      }
    } catch (err) {
      console.error('Reorder save failed:', err);
    }
  }, [trucks, localUpdateTruck]);

  // ── Card-level RO actions (close / void without opening the RO page) ─────

  const currentRole = getCurrentRole();
  const canClose = hasPermission(currentRole, PERMISSIONS.CLOSE_WORK_ORDERS);
  const canVoid  = hasPermission(currentRole, PERMISSIONS.VOID_WORK_ORDERS);
  const canEdit  = hasPermission(currentRole, PERMISSIONS.EDIT_WORK_ORDERS);
  const canNote  = hasPermission(currentRole, PERMISSIONS.ADD_NOTES);
  const [editTruck,   setEditTruck]   = useState(null);
  const [assignTruck, setAssignTruck] = useState(null);
  const [noteTruck,   setNoteTruck]   = useState(null);

  const handleEditSave = useCallback(async (updatedData) => {
    if (!editTruck) return;
    localUpdateTruck(editTruck.id, updatedData);
    setEditTruck(null);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: editTruck.id, ...updatedData } },
      });
    } catch (err) {
      console.error('[FlowView] edit save failed:', err);
      alert('Failed to save RO changes.');
    }
  }, [editTruck, localUpdateTruck]);

  const handleAssignTechs = useCallback(async (techNumbers) => {
    if (!assignTruck) return;
    localUpdateTruck(assignTruck.id, { techNumbers });
    setAssignTruck(null);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: assignTruck.id, techNumbers } },
      });
    } catch (err) {
      console.error('[FlowView] assign tech failed:', err);
    }
  }, [assignTruck, localUpdateTruck]);

  const handleAddNote = useCallback(async (note) => {
    if (!noteTruck || !note) return;
    const now = nowSeconds();
    const patch = {
      notes: [...(noteTruck.notes || []), `,${note}`],
      lastNote: now,
      lastStatusChange: now,
      warning: false,
      expired: false,
      critical: false,
    };
    localUpdateTruck(noteTruck.id, patch);
    setNoteTruck(null);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: noteTruck.id, ...patch } },
      });
      recordTruckEvent(noteTruck, EVENT_TYPES.NOTE_ADDED, { timestamp: now, note });
    } catch (err) {
      console.error('[FlowView] add note failed:', err);
    }
  }, [noteTruck, localUpdateTruck]);

  const finishRO = useCallback(async (truck, newStatus, eventType) => {
    const ts = nowSeconds();
    const cleanHistory = (truck.statusHistory || [])
      .filter(e => e?.status && e?.timeEntered)
      .map(e => ({ status: e.status, timeEntered: e.timeEntered }));
    const patch = {
      status: newStatus,
      completionTime: ts,
      lastStatusChange: ts,
      statusHistory: [...cleanHistory, { status: newStatus, timeEntered: ts }],
    };
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
      recordTruckEvent(truck, eventType, { timestamp: ts, previousStatus: truck.status, newStatus, metadata: { moveSource: 'flow-card-menu' } });
    } catch (err) {
      console.error('[FlowView] card action failed:', err);
    }
  }, [localUpdateTruck]);

  const handleCloseRO = useCallback((truck) => finishRO(truck, 'completed', EVENT_TYPES.COMPLETED), [finishRO]);
  const handleVoidRO  = useCallback((truck) => finishRO(truck, 'voided', EVENT_TYPES.VOIDED), [finishRO]);

  // Mark every part line received from the board (same result as receiving
  // them one by one on the RO page). Doesn't touch blockers — the RO page
  // owns that prompt.
  const handlePartsIn = useCallback(async (truck) => {
    const ts = nowSeconds();
    const updatedParts = stripTypename(truck.parts || []).map(p => (
      p.status === 'received' ? p : { ...p, status: 'received', receivedAt: ts }
    ));
    const activity = {
      id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'note',
      text: 'All parts received — marked from Flow board',
      author: getCurrentRole(),
      timestamp: ts,
      photos: [],
      customerFacing: false,
      category: null,
      stage: truck.status || null,
    };
    const patch = {
      parts: updatedParts,
      partsStatus: 'READY',
      activities: [activity, ...stripTypename(truck.activities || [])],
      lastNote: ts,
    };
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) {
      console.error('[FlowView] parts-in failed:', err);
    }
  }, [localUpdateTruck]);

  // ── Board-level stats ──────────────────────────────────────────────────────

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flow-view">

      {/* ── Toolbar: lens switcher + fullscreen + new truck (56px) ── */}
      <div className="fv-toolbar">
        <div className="fv-toolbar-filters">
          <FlowSwitcher bare />
        </div>

        <div className="fv-toolbar-actions">
          <FullscreenBtn />

          {/* New Truck — primary blue CTA */}
          {onAddTruck && (
            <button className="fv-new-btn" onClick={onAddTruck}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Truck
            </button>
          )}
        </div>
      </div>

      {/* Stage columns */}
      <div className="flow-columns">
        {displayed.map(({ stage, trucks: stageTrucks }) => {
          const accentColor = STAGE_COLORS[stage.id] || '#64748B';
          const laborHrs = stageTrucks.reduce((sum, t) => {
            return sum + (t.repairCategories || []).reduce((s, c) => s + (parseFloat(c.estimatedTime) || 0), 0);
          }, 0);
          const laborStr = laborHrs > 0
            ? `${laborHrs % 1 === 0 ? laborHrs : laborHrs.toFixed(1)} HRS`
            : null;

          return (
            <div
              key={stage.id}
              className={`flow-col${activeStage === stage.id ? ' flow-col--active' : ''}${dragOverStage === stage.id ? ' flow-col--dragover' : ''}`}
              onDragOver={(e) => handleColDragOver(e, stage.id)}
              onDragLeave={handleColDragLeave}
              onDrop={(e) => handleColDrop(e, stage)}
            >
              <button
                className="flow-col-header"
                style={{ '--col-accent': accentColor }}
                onClick={() => setActiveStage(prev => prev === stage.id ? null : stage.id)}
              >
                <span className="flow-col-name" style={{ color: accentColor }}>{stage.label.toUpperCase()}</span>
                <span className="flow-col-count" style={{ background: `${accentColor}18`, color: accentColor }}>{stageTrucks.length}</span>
                {laborStr && <span className="flow-col-labor">{laborStr}</span>}
                {stage.statuses.includes('parts-hold') && stageTrucks.filter(partsIn).length > 0 && (
                  <span className="flow-col-partsin" title="All parts received — waiting on a tech">
                    {stageTrucks.filter(partsIn).length} IN
                  </span>
                )}
              </button>

              <div className="flow-col-cards">
                {stageTrucks.length === 0 && (
                  <div className="flow-col-empty">
                    {dragOverStage === stage.id ? 'Release to move here' : 'Empty'}
                  </div>
                )}
                {stageTrucks.map(t => (
                  <FlowCard
                    key={t.id}
                    truck={t}
                    isDragging={draggingId === t.id}
                    dropIndicator={
                      dragOverCardId === t.id && t.id !== draggingId
                        ? (insertBefore ? 'before' : 'after')
                        : null
                    }
                    onDragStart={(e) => handleDragStart(e, t)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleCardDragOver(e, t.id, stage.id)}
                    onDragLeave={handleCardDragLeave}
                    onDrop={(e) => handleCardDrop(e, t, stage)}
                    canClose={canClose}
                    canVoid={canVoid}
                    canEdit={canEdit}
                    canNote={canNote}
                    onCloseRO={handleCloseRO}
                    onVoidRO={handleVoidRO}
                    onEditRO={setEditTruck}
                    onAssignTech={setAssignTruck}
                    onAddNote={setNoteTruck}
                    onPartsIn={handlePartsIn}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {editTruck && (
        <NewROModal
          editMode={true}
          truckData={editTruck}
          onClose={() => setEditTruck(null)}
          onSubmit={handleEditSave}
        />
      )}

      {assignTruck && (
        <AssignTechModal
          onClose={() => setAssignTruck(null)}
          onSubmit={handleAssignTechs}
          currentTechs={assignTruck.techNumbers || [assignTruck.techNumber].filter(Boolean)}
        />
      )}

      {noteTruck && (
        <NoteModal
          onClose={() => setNoteTruck(null)}
          onSubmit={handleAddNote}
          currentNotes={noteTruck.notes || []}
        />
      )}
    </div>
  );
}
